import {
  AfterViewChecked,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Select } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { UserModel } from '@app/modules/user/model/user';
import { UserState } from '@app/modules/user/store/user.state';
import { AcpService } from '@app/modules/acp/service/acp.service';
import { CareGuideChatMessage, CareGuideChatService, MAX_MESSAGE_LENGTH } from './care-guide-chat.service';

/** Only these route prefixes show the widget (patient portal + ACP). */
const PATIENT_ROUTE_PREFIXES = ['/office/portal', '/office/acp', '/office/living-will'];

/**
 * Floating "Care Guide" chat widget for PATIENT portal routes.
 *
 * Mounted once in PrivateLayoutComponent (alongside the patient sidebar) and
 * self-gating: it renders nothing unless the signed-in user is a patient AND
 * the current route is /office/portal/** or /office/acp/** — so doctor, admin
 * and proxy layouts are untouched. Conversation state lives in
 * CareGuideChatService (session memory only).
 */
@Component({
  selector: 'app-care-guide-chat',
  templateUrl: './care-guide-chat.component.html',
  styleUrls: ['./care-guide-chat.component.sass'],
})
export class CareGuideChatComponent implements OnInit, OnDestroy, AfterViewChecked {
  private _subscription = new Subscription();

  /** Set when the message list should stick to the bottom on next render. */
  private _scrollPending = false;

  private _renderedMessageCount = 0;

  @Select(UserState.model) public user$: Observable<UserModel>;

  @ViewChild('messageList') private messageListRef: ElementRef<HTMLElement>;

  @ViewChild('chatInput') private chatInputRef: ElementRef<HTMLTextAreaElement>;

  @ViewChild('launcher') private launcherRef: ElementRef<HTMLButtonElement>;

  public onPatientRoute = false;

  public draft = '';

  public readonly maxLength = MAX_MESSAGE_LENGTH;

  /** Chips shown on an empty conversation — a rotating window of 4 from the
   *  backend's 10 starter questions (static defaults until they load). */
  public starters: string[] = [
    'What does this site do?',
    'What is CPR?',
    'What is a health care agent?',
    'How is my plan protected?',
  ];

  /** Full starter list from GET /care-guide/starters (lazy, on first open). */
  private _allStarters: string[] = [];

  private _starterOffset = 0;

  private _startersRequested = false;

  constructor(
    public readonly guide: CareGuideChatService,
    private readonly router: Router,
    private readonly acp: AcpService,
  ) {}

  public ngOnInit(): void {
    this.onPatientRoute = this.isPatientRoute(this.router.url);

    this._subscription.add(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe((event: NavigationEnd) => {
          this.onPatientRoute = this.isPatientRoute(event.urlAfterRedirects || event.url);
        }),
    );

    // Focus the input whenever the panel opens (incl. from the Help page).
    this._subscription.add(
      this.guide.open$.subscribe((open) => {
        if (open) {
          this.rotateStarters();
          this._scrollPending = true;
          setTimeout(() => this.focusInput());
        }
      }),
    );
  }

  public ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  public ngAfterViewChecked(): void {
    const count = this.guide.messages.length + (this.guide.sending ? 1 : 0);

    if (count !== this._renderedMessageCount) {
      this._renderedMessageCount = count;
      this._scrollPending = true;
    }

    if (this._scrollPending && this.messageListRef) {
      const el = this.messageListRef.nativeElement;
      el.scrollTop = el.scrollHeight;
      this._scrollPending = false;
    }
  }

  public open(): void {
    // Resolve the current ACP topic BEFORE showing chips so suggestions are
    // topic-aware (approved plan §15/§16). Resolution is local/cached — it
    // never triggers a model call; only Send / chip clicks do.
    this.resolveTopic(() => {
      this._allStarters = [];
      this._startersRequested = false;
      this.rotateStarters();
    });
    this.guide.open();
  }

  /** moduleKey→question keys, cached for the session after one content GET. */
  private _acpQuestionKeys: Record<string, string[]> | null = null;

  /**
   * Map the current URL to the ACP question the patient has open:
   * /office/acp/:moduleKey?step=N → that module's Nth question key.
   * Anything else clears the topic. Never sends answers or record data.
   */
  private resolveTopic(done: () => void): void {
    const url = this.router.url;
    const match = url.match(/\/office\/acp\/([a-z0-9-]+)(?:\?|$)/i);
    const stepMatch = url.match(/[?&]step=(\d+)/);
    if (!match) {
      this.guide.topic = null;
      this.guide.questionId = null;
      done();
      return;
    }
    const moduleKey = match[1];
    const step = stepMatch ? Number(stepMatch[1]) : 1;
    const apply = () => {
      const keys = (this._acpQuestionKeys && this._acpQuestionKeys[moduleKey]) || [];
      const key = keys[step - 1] || null;
      this.guide.topic = key;
      this.guide.questionId = key;
      done();
    };
    if (this._acpQuestionKeys) {
      apply();
      return;
    }
    this._subscription.add(
      this.acp.getContent().subscribe(
        (content) => {
          const map: Record<string, string[]> = {};
          for (const module of content.modules || []) {
            map[module.key] = (module.questions || []).map((question) => question.key);
          }
          this._acpQuestionKeys = map;
          apply();
        },
        () => {
          this.guide.topic = null;
          this.guide.questionId = null;
          done();
        },
      ),
    );
  }

  public close(): void {
    this.guide.close();
    // Return focus to the launcher so keyboard users don't lose their place.
    setTimeout(() => {
      if (this.launcherRef) {
        this.launcherRef.nativeElement.focus();
      }
    });
  }

  public send(): void {
    if (this.guide.sending) {
      return;
    }

    const text = this.draft;
    this.draft = '';
    this.guide.send(text);
    this.focusInput();
  }

  public sendStarter(question: string): void {
    if (this.guide.sending) {
      return;
    }

    this.guide.send(question);
    this.focusInput();
  }

  /** Enter sends; Shift+Enter inserts a newline. */
  public onInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  public trackByIndex(index: number, _message: CareGuideChatMessage): number {
    return index;
  }

  public get canSend(): boolean {
    return !this.guide.sending && this.draft.trim().length > 0;
  }

  private focusInput(): void {
    if (this.chatInputRef) {
      this.chatInputRef.nativeElement.focus();
    }
  }

  /**
   * Fetches the backend starter list on first open, then advances a window of
   * 4 on every open so returning users see fresh suggestions. Keeps the static
   * defaults if the request fails.
   */
  private rotateStarters(): void {
    if (this._allStarters.length === 0) {
      if (this._startersRequested) {
        return;
      }
      this._startersRequested = true;
      this._subscription.add(
        this.guide.getStarters().subscribe(
          (response) => {
            if (response && Array.isArray(response.starters) && response.starters.length > 0) {
              this._allStarters = response.starters;
              this.starters = this.nextStarterWindow();
            }
          },
          () => {
            // Keep the static defaults; try again on a later open.
            this._startersRequested = false;
          },
        ),
      );
      return;
    }

    this.starters = this.nextStarterWindow();
  }

  private nextStarterWindow(): string[] {
    const window: string[] = [];
    for (let i = 0; i < 4 && i < this._allStarters.length; i++) {
      window.push(this._allStarters[(this._starterOffset + i) % this._allStarters.length]);
    }
    this._starterOffset = (this._starterOffset + 4) % (this._allStarters.length || 1);
    return window;
  }

  private isPatientRoute(url: string): boolean {
    const path = (url || '').split('?')[0];

    return PATIENT_ROUTE_PREFIXES.some(
      (prefix) => path === prefix || path.startsWith(`${prefix}/`),
    );
  }
}
